import React, { useEffect, useMemo, useState } from 'react';
import { Box, Divider, Popover, Typography, Button } from '@mui/material';
import _ from 'lodash';
import { getStatusText, getStatusColor, getDisplayName, IsAgileExecutive } from '../../components/common';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowForwardIosOutlinedIcon from '@mui/icons-material/ArrowForwardIosOutlined';
import { DeleteIcon } from '../../../../utils/Icons';
import { API } from '../../../../api';
import { toast } from 'react-toastify';
import WelcomeModals from './WelcomeModals';
import ObjectiveModal from './ObjectiveModal';
import { useAuth0 } from '@auth0/auth0-react';
import UserPeersPopup from './UserPeersPopup';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import MatchCancelModal from './MatchCancelModal';
import { navigateToChat } from '../NavigateToChat';
import CallBookingModal from '../CallBookingModal';
import { useModule } from '../../../../utils/useModule';
import AppLoader from '../../../../components/AppLoader';
import { getUserDetails } from '../../../../utils/orgName';
import useGroupDetails from '../../../../queries/useGroupDetails';
import useLearningObjs from '../../../../queries/useLearningObjs';
import useProfileDetails from '../../../../hooks/useProfileDetails';
import { getAcsUserByCommunicationId } from '../../../../routes/helpers';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { taskActions } from '../../../../redux/slices/goals-tasks/getTasksSlice';
import { RequestMatch, RequestMatchReject } from '../Matches/components/RequestAndRejectMatch';
import { eventTracking, events } from '../../../../utils/amplitude';
import RecruitNewMentor from './RecruitNewMentor';
import UserProfileDetailsPopup from '../../components/UserProfileDetailsPopup';
import { PlusOutlined } from '@ant-design/icons';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import DosenEllipsisMenu from './MentorDash/Components/DosenElipsisMenu';
import MenteeProfileModal from './MentorDash/Components/MenteeProfileModal';
import ForumIcon from '@mui/icons-material/Forum';

const calculateMenteeStats = (mentee: any) => {
  if (!mentee) {
    return {
      messagesShared: 0,
      meetingHistory: 0,
      futureMeetings: 0
    };
  }
  const meetings = Array.isArray(mentee?.meetings) ? mentee.meetings : [];
  let pastMeetings = 0;
  let futureMeetings = 0;
  const now = Date.now();
  meetings.forEach((meeting: any) => {
    const callDetails = meeting?.callDetails || meeting;
    const callDate = callDetails?.callDate ? new Date(callDetails.callDate).getTime() : null;
    if (!callDate || Number.isNaN(callDate)) {
      return;
    }
    if (callDate >= now) {
      futureMeetings += 1;
    } else {
      pastMeetings += 1;
    }
  });
  const messagesShared =
    mentee?.messagesShared ??
    mentee?.totalMessages ??
    mentee?.messagesCount ??
    mentee?.messageCount ??
    (Array.isArray(mentee?.messages) ? mentee.messages.length : 0) ??
    0;
  return {
    messagesShared: messagesShared || 0,
    meetingHistory: pastMeetings,
    futureMeetings
  };
};

const Mentor = () => {
  /** The Agile Executive checking */
  const isAgileExec = IsAgileExecutive();
  // const isStarVentureExec = IsStarVenture();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user } = useAuth0();
  const { location, orgId } = getUserDetails();
  const { isMasterclass, isMentorship, isHybrid } = useModule(); // Fetching current module from Redux state
  // const orgDetails = useAppSelector((state) => state.orgDetails);
  // const allGroupsTasks = useAllGroupTasks();
  // const taskData = allGroupsTasks.data ? _.last(allGroupsTasks.data) : [];
  // const groupIds = _.get(taskData, '_id');
  // const levels = _.get(taskData, 'levels');
  // const levelId = _.get(_.head(levels), '_id');
  // const tasks = _.get(_.head(levels), 'tasks');
  // const taskId = _.get(_.head(tasks), '_id');
  const [loadingMentor, setLoadingMentor] = useState('');
  const [peersList, setPeersList] = useState<any>();
  const [connectionCount, setConnectionCount] = useState('');
  const [myMenteesList, setMyMenteesList] = useState<any>([]);
  const [myRequestingMenteesList, setRequestingMenteesList] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedMember, setSelectedMember] = useState<any>(null);
  const avatars = peersList?.map((each: any) => ({ avatar: each?.headshot, firstName: each?.displayName }));
  const [displayName, setDisplayName] = useState('');
  const [participantUser, setParticipantUser] = useState<any>([]);
  const [mentorshipHoursValue, setMentorshipHours] = useState('00:00');
  const [mentorRoi, setMentorRoi] = useState<any>(0);
  const personalThreadsArr = useAppSelector((state) => state.acsChannels.personalThreadsArr);
  const selectedGroupId = useAppSelector((state) => state.selectedGroup.data);
  const communityMembers = useAppSelector((state) => state.acsCommunityUsers.data);
  const commList = useAppSelector((state) => state.getCommunityMembers.data);
  /** Show goal progress */
  const [hasShowGoalProgressValue, setHasShowGoalProgressValue] = useState<any>();
  const groupId = selectedGroupId.id || '';
  const groupList = useAppSelector((state) => state.groupRoleList);
  const [isPeersPopupOpen, setPeersPopupOpen] = React.useState(false);
  /** For responsive */
  const [openCalendarModal, setOpenCalendarModal] = useState(false);
  const [bookedCallDetails, setBookedCallDetails] = useState<any>('');

  const [recruitModalOpen, setRecruitModalOpen] = useState(false);
  const [recruitMentors, setRecruitMentors] = useState<any[]>([]);
  const [selectedPopoverMentee, setSelectedPopoverMentee] = useState<any>(null);
  const [recruitMentorsLoading, setRecruitMentorsLoading] = useState(false);
  const [recruitObjId, setRecruitObjId] = useState('');
  const [recruitObjectiveName, setRecruitObjectiveName] = useState('');
  const [expandedObjectives, setExpandedObjectives] = useState<Record<string, boolean>>({});
  const [openUserProfileDetails, setOpenUserProfileDetails] = React.useState(false);
  const [selectedMenteeInfo, setSelectedMenteeInfo] = useState(null);
  const handleopenUserProfileDetails = (flag: boolean) => setOpenUserProfileDetails(flag);

  const {
    data: menteeList,
    isLoading: myMenteeLoading,
    error,
    isFetching,
    refetch
  } = useQuery({
    queryKey: ['getMentorsPeersHomePage'],
    queryFn: () => {
      if (!groupId) {
        return;
      }
      return API.getMyMentees({ orgId, groupId });
    },
    staleTime: 10 * 10 * 1000
  });
  const { userId: objUserId, gettingStarted } = useProfileDetails();

  /** Welcome Modals */

  const [openGettingStartedModal, setOpenGettingStartedModal] = useState(false);
  const handleClickWelcomeModals = (flag: boolean) => {
    setOpenGettingStartedModal(flag);
  };
  useEffect(() => {
    if (!gettingStarted) {
      setOpenGettingStartedModal(true);
    }
  }, [gettingStarted]);

  /** For request and reject match dialog */
  const [openRequestMatch, setOpenRequestMatch] = useState(false);
  const [openRequestMatchReject, setOpenRequestMatchReject] = useState(false);
  const [isObjectiveModalOpen, setObjectiveModalOpen] = useState(false);
  const [requestId, setRequestId] = useState('');
  const [mentorId, setMentorId] = useState('');
  const [menteeId, setMenteeId] = useState('');
  const [commId, setCommId] = useState('');
  const [actionId, setActionId] = useState('');
  const [reloadFlag, setReloadFlag] = useState(false);
  const handleRequestMatch = (flag: boolean) => setOpenRequestMatch(flag);
  const handleRequestMatchReject = (flag: boolean) => setOpenRequestMatchReject(flag);
  const [objective, setObjective] = useState<any>({});
  const [selectedMentee, setSelectedMentee] = useState();
  const [matchedObjectives, setMatchedObjectives] = useState([]);
  const [activeExpertiseIndex, setActiveExpertiseIndex] = useState(0);
  const handleCloseObjectiveModal = () => {
    setObjectiveModalOpen(false);
    getObjectiveMentees({ orgId, location, groupId });
  };
  /** Popover */
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openMatchCancel, setOpenMatchCancel] = useState(false);
  const [selectedMatchId, setSelectedMatchId] = useState<string>('');
  const [recentlyScheduledMeeting, setRecentlyScheduledMeeting] = useState(null);
  const handleOpenMatchCancel = (matchId: string) => {
    setSelectedMatchId(matchId);
    setOpenMatchCancel(true);
    setAnchorEl(null); // Close popover when modal opens
  };
  const handleCloseMatchCancel = async () => {
    setOpenMatchCancel(false);

    if (isMentorship) {
      await getObjectiveMentees({ orgId, location, groupId });
    } else {
      await getMyMenteesList({ orgId, groupId });
    }
  };
  const handlePopoverClose = () => {
    setAnchorEl(null);
  };
  const openMatchesTablePopover = Boolean(anchorEl);
  const idMatchesTablePopover = openMatchesTablePopover ? 'matches-popover' : undefined;
  /** Handle User hotes popup */
  // const [openUserNotes, setOpenUserNotes] = useState(false);
  // const handleOpenUserNotes = (flag: boolean) => setOpenUserNotes(flag);

  // const [groupId, setGroupId] = useState('');
  // const userLastseen = localStorage.getItem('lastseen');
  // const lastSeenRole = userLastseen ? JSON.parse(userLastseen) : null;

  //   useEffect(() => {
  //     dispatch(updateSelectedId(lastSeenRole));
  //   }, []);

  //   useEffect(() => {
  //     if (selectedGroupId) {
  //       setGroupId(selectedGroupId?.id);
  //     }
  //   }, [selectedGroupId]);

  const rejectMatchRequest = (requestId: string, mentorId: string, menteeId: string, commId: string) => {
    setRequestId(requestId);
    setMentorId(mentorId);
    setMenteeId(menteeId);
    setCommId(commId);
    handleRequestMatchReject(true);
  };

  const doReload = () => {
    setLoading(false);
    setReloadFlag(true);
  };

  useEffect(() => {
    if (reloadFlag) {
      setReloadFlag(false);
    }
  }, [reloadFlag]);
  const [requestLoading, setRequestLoading] = useState(false);

  const acceptMatchRequest = async (requestId: string, mentorId: string, menteeId: string) => {
    const grpId = groupId;
    setActionId(requestId);
    setLoading(true);
    setRequestLoading(true);
    try {
      const response = await API.acceptMatchRequest({ grpId, mentorId, menteeId, requestId });
      if (response.status === 200) {
        eventTracking(events.matchByRequest, {});
        await refetch();
      }
    } catch (e) {
    } finally {
      setActionId('');
      setLoading(false);
      setRequestLoading(true);
    }
  };

  const removeMatchRequest = async (requestId: string) => {
    const grpId = groupId;
    setLoading(true);
    setRequestLoading(true);
    try {
      const response = await API.removeMatchRequest({ grpId, requestId });
      if (response.status === 200) {
        await refetch();
      }
    } catch (e) {
    } finally {
      setRequestLoading(false);
      setLoading(false);
    }
  };

  const handlePeersPopup = (open: boolean) => {
    setPeersPopupOpen(open);
  };

  const navigateMember = (member: any) => {
    setSelectedMember(member);
  };

  const handleCalendarOpen = (member: any) => {
    setDisplayName(member.displayName);
    setParticipantUser([member]);
    if (isMentorship) {
      setObjective(member.matchedObjectives[0]);
    }
    setRecentlyScheduledMeeting(null);
    setBookedCallDetails('');
    setOpenCalendarModal(true);
  };

  const handleMeetingScheduled = (meetingDetails: any) => {
    setRecentlyScheduledMeeting(meetingDetails);
    fetchGroupDetails();
  };

  const handleCalendarClose = () => {
    setDisplayName('');
    setParticipantUser([]);
    setBookedCallDetails('');
    setOpenCalendarModal(false);
  };

  const moveToUsercommunityProfile = (userId: string) => {
    const mem = communityMembers.find((item: { userId: string }) => item.userId === userId);
    const modifiedMem = { ...mem, id: _.get(mem, 'id.coId', '') };
    const allCommunityMembersList = communityMembers;

    if (mem) {
      navigate('/app/communitymember', {
        state: { mem: modifiedMem, allCommunityMembersList: commList }
      });
    }
  };

  const getMentorsPeersHomePage = async (orgId: string, location: string, groupId: string) => {
    try {
      const response = await API.getMentorsPeersHomePage(orgId, location, groupId);
      if (response.status === 200) {
        setPeersList(response?.data?.data);
        setConnectionCount(response?.data?.connections);
        setLoading(false);
      } else {
        setLoading(true);
      }
    } catch (e) {
      setLoading(true);
    }
  };

  useEffect(() => {
    if ((menteeList?.data?.data?.length > 0 || menteeList) && !isMentorship) {
      //setMyMenteesList(menteeList?.data?.data);
      setRequestingMenteesList(menteeList?.data?.reqestedMentees);
      setHasShowGoalProgressValue(menteeList?.data?.showGoalProgess);
    }
  }, [menteeList]);

  const getMyMenteesList = async ({ orgId, groupId }: { orgId: string; groupId: string }) => {
    try {
      const response = await API.getMyMentees({ orgId, groupId });
      if (response.status === 200) {
        setMyMenteesList(response?.data?.data);
        setRequestingMenteesList(response?.data?.reqestedMentees);
        setHasShowGoalProgressValue(response?.data?.showGoalProgess);
      } else {
        // toast.error('Mantors list failed to load');
      }
    } catch (e) {
      // toast.error('Mantors list failed to load');
    }
  };

  const grpDetails = useAppSelector((state) => state.groupDetails.data);
  const sessionsPerMatch = grpDetails?.matchesSettings?.sessionsPerMatch || 1;
  const getLiveSessions = (length: number) => {
    const value = Math.round((length / sessionsPerMatch) * 100);
    return isNaN(value) ? 0 : value > 100 ? 100 : value;
  };
  const getObjectiveMentees = async ({
    orgId,
    location,
    groupId
  }: {
    orgId: string;
    location: string;
    groupId: string;
  }) => {
    try {
      const response = await API.getMenteeListForMentor(orgId, location, groupId);
      if (response.status === 200) {
        const transformedData = response?.data?.data.map((item: any) => ({
          ...item,
          displayName: item.menteeName
        }));
        setMyMenteesList(transformedData);
      }
    } catch (e) {
      console.error('Failed to fetch mentees:', e);
    }
  };

  const {
    data: objectives,
    isLoading: objLoading,
    refetch: fetchObjectives,
    isError: objError
  } = useLearningObjs({ orgId, grpId: groupId });

  const learningObjectives = objectives?.data?.data || [];

  const getObjectiveId = (obj: any, index: number) =>
    obj?.objectiveId || obj?.id || obj?._id || `${obj?.objective || 'objective'}-${index}`;

  const programLearningObjectives = learningObjectives;
  useEffect(() => {
    if (!programLearningObjectives.length) {
      setActiveExpertiseIndex(0);
      return;
    }
    setActiveExpertiseIndex((prev) =>
      prev >= programLearningObjectives.length ? programLearningObjectives.length - 1 : prev
    );
  }, [programLearningObjectives.length]);

  const objectiveMentees = useMemo(() => {
    const map: Record<string, any[]> = {};
    (myMenteesList || []).forEach((mentee: any) => {
      const matched = mentee?.matchedObjectives || [];
      matched.forEach((obj: any) => {
        const id = obj?.objectiveId || obj?.id || obj?._id || obj?.objective;
        if (!id) return;
        if (!map[id]) map[id] = [];
        map[id].push({
          ...mentee,
          objective: obj?.objective || obj?.name
        });
      });
    });
    return map;
  }, [myMenteesList]);

  const activeObjective = programLearningObjectives[activeExpertiseIndex];
  const activeObjectiveId = activeObjective ? getObjectiveId(activeObjective, activeExpertiseIndex) : '';
  const menteesForActiveObjective = activeObjectiveId ? objectiveMentees[activeObjectiveId] || [] : [];
  const primaryMentee = menteesForActiveObjective[0] || null;
  const primaryObjectiveStatus = primaryMentee?.matchedObjectives?.[0]?.status || 'to-do';
  const normalizedPrimaryStatus = primaryObjectiveStatus?.toString()?.toLowerCase() || '';
  const isObjectiveComplete = ['completed', 'complete', 'done'].includes(normalizedPrimaryStatus);
  const menteeStatusLabel = getStatusText(primaryObjectiveStatus);
  const menteeStatusColor = getStatusColor(primaryObjectiveStatus);
  const menteeNameParts = primaryMentee ? getDisplayName(primaryMentee).split(' ') : [];
  const menteeStats = useMemo(() => calculateMenteeStats(primaryMentee), [primaryMentee]);
  const menteeOrdinal =
    primaryMentee && menteesForActiveObjective.length
      ? menteesForActiveObjective.findIndex(
          (mentee) =>
            mentee?.id === primaryMentee?.id ||
            mentee?.menteeId === primaryMentee?.menteeId ||
            mentee?.displayName === primaryMentee?.displayName
        )
      : -1;
  const menteeLabel = menteeOrdinal >= 0 ? `Mentee ${menteeOrdinal + 1}` : 'Mentee 1';
  const expertiseLabel = `Area of Expertise ${activeExpertiseIndex + 1}`;
  const expertiseTitle = activeObjective?.objective || `Objective ${activeExpertiseIndex + 1}`;
  const canGoNext = activeExpertiseIndex < programLearningObjectives.length - 1;
  const canGoPrev = activeExpertiseIndex > 0;
  const handleNextExpertise = () => {
    setActiveExpertiseIndex((prev) => Math.min(prev + 1, Math.max(programLearningObjectives.length - 1, 0)));
  };
  const handlePrevExpertise = () => {
    setActiveExpertiseIndex((prev) => Math.max(prev - 1, 0));
  };

  useEffect(() => {
    if (!programLearningObjectives.length) return;
    const firstId = getObjectiveId(programLearningObjectives[0], 0);
    setExpandedObjectives((prev) => (prev[firstId] ? prev : { [firstId]: true }));
  }, [programLearningObjectives]);

  const handleFindNewMentee = (objId: string, name: string) => {
    setRecruitObjId(objId);
    setRecruitObjectiveName(name);
    setRecruitModalOpen(true);
  };

  const renderMenteeCard = () => {
    const handleAddClick = () => {
      if (!activeObjectiveId) return;
      handleFindNewMentee(activeObjectiveId, expertiseTitle);
    };

    if (!primaryMentee) {
      return (
        <Box
          sx={{
            width: 344,
            height: 321,
            p: 5,
            background: 'var(--Fill-Card-card-highlight, #1C1C1C)',
            borderRadius: 1.5,
            outline: '1px solid var(--Stroke-Card-card-highlight, #FDFDFD)',
            outlineOffset: '-1px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2.5,
            cursor: activeObjectiveId ? 'pointer' : 'not-allowed'
          }}
          onClick={activeObjectiveId ? handleAddClick : undefined}
        >
          <Typography
            sx={{
              fontSize: 22,
              fontWeight: 600,
              color: '#FDFDFD',
              textTransform: 'capitalize'
            }}
          >
            Add Mentee
          </Typography>
          <Box
            sx={{
              width: 34,
              height: 34,
              // borderRadius: '50%',
              background: '#1C1C1C',
              // border: '1px solid var(--Stroke-Card-card-highlight, #FDFDFD)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <PlusOutlined color="white" style={{color: 'white'}} size={16} />
          </Box>
        </Box>
      );
    }

    const avatarUrl =
      primaryMentee?.headshot ||
      primaryMentee?.photo ||
      primaryMentee?.picture ||
      primaryMentee?.displayPicture ||
      'https://placehold.co/50x50';

    if (isObjectiveComplete) {
      return (
        <Box
          sx={{
            width: 344,
            minHeight: 321,
            p: 5,
            background: 'rgba(0, 150.73, 45.45, 0.20)',
            borderRadius: 1.5,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            overflow: 'hidden'
          }}
        >

          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1
            }}
          >
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#FDFDFD' }}>{menteeLabel}</Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                flexWrap: 'wrap'
              }}
            >
              {menteeNameParts.length ? (
                <>
                  <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#FDFDFD' }}>{menteeNameParts[0]}</Typography>
                  <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#FDFDFD' }}>
                    {menteeNameParts.slice(1).join(' ') || ''}
                  </Typography>
                </>
              ) : (
                <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#FDFDFD' }}>Mentor a mentee</Typography>
              )}
            </Box>
          </Box>
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 60,
                p: 1.5,
                background: 'rgba(0, 0, 0, 0.5)',
                borderRadius: 1.25,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Typography sx={{ color: '#FDFDFD', fontWeight: 600, fontSize: 14 }}>Objective Complete</Typography>
            </Box>
            <Box
              sx={{
                // padding: 6,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '999px',
                  background: '#00972D',
                  outline: '6px solid rgba(255, 255, 255, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <CheckIcon sx={{ color: '#FDFDFD', fontSize: 24 }} />
              </Box>
            </Box>
          </Box>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          width: 344,
          minHeight: 321,
          p: 3,
          background: 'var(--Fill-Card-card-highlight, #1C1C1C)',
          borderRadius: 1.5,
          outline: '1px solid var(--Stroke-Card-card-highlight, #FDFDFD)',
          outlineOffset: '-1px',
          display: 'flex',
          flexDirection: 'column',
          gap: 3
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25
          }}
        >
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: '50%',
              overflow: 'hidden',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}
          >
            <Box
              component="img"
              src={avatarUrl}
              alt={`${getDisplayName(primaryMentee)} avatar`}
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5
            }}
          >
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#B6B6B6' }}>{menteeLabel}</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {menteeNameParts.length ? (
                <>
                  <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#FDFDFD' }}>{menteeNameParts[0]}</Typography>
                  <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#FDFDFD' }}>
                    {menteeNameParts.slice(1).join(' ') || ''}
                  </Typography>
                </>
              ) : (
                <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#FDFDFD' }}>Add your first mentee</Typography>
              )}
            </Box>
          </Box>
          <DosenEllipsisMenu onViewProfile={() => handleProfileClick(primaryMentee)} />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              background: 'var(--Fill-background, #0F0F0F)',
              borderRadius: 1.25,
              p: 1.5
            }}
          >
            <Box flex={1} display="flex" flexDirection="column" gap={0.5}>
              <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#FDFDFD' }}>
                {menteeStats.messagesShared}
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#868686' }}>Messages Shared</Typography>
            </Box>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '999px',
                background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative'
              }}
            >
             <ForumIcon sx={{color: 'white'}}/>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              background: 'var(--Fill-background, #0F0F0F)',
              borderRadius: 1.25,
              p: 1.5
            }}
          >
            <Box flex={1} display="flex" flexDirection="column" gap={0.5}>
              <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#FDFDFD' }}>
                {menteeStats.meetingHistory}
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#868686' }}>Meeting History</Typography>
            </Box>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 17,
                background: 'var(--Fill-Button-button-gray, #1C1C1C)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  border: '2px solid #FDFDFD',
                  borderRadius: '50%'
                }}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              background: 'var(--Fill-background, #0F0F0F)',
              borderRadius: 1.25,
              p: 1.5
            }}
          >
            <Box flex={1} display="flex" flexDirection="column" gap={0.5}>
              <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#FDFDFD' }}>
                {menteeStats.futureMeetings}
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#868686' }}>Future Meetings</Typography>
            </Box>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 17,
                background: 'var(--Fill-Button-button-gray, #1C1C1C)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  border: '2px solid #FDFDFD',
                  borderRadius: '50%'
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderHybridMatchesList = () => {
    const matches = myMenteesList || [];
    if (!matches.length) {
      return (
        <Box
          sx={{
            width: '100%',
            minHeight: 200,
            borderRadius: 1.5,
            border: '1px solid #2A2A2A',
            background: 'var(--Fill-Background-secondary, #101010)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Typography sx={{ color: '#868686', fontSize: 14 }}>Nenhum match ativo ainda.</Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
      >
        {matches.map((mentee: any, index: number) => {
          const key = mentee?.id || mentee?.menteeId || mentee?.matchId || index;
          const statusValue = mentee?.matchStatus || mentee?.status || 'to-do';
          const statusLabel = getStatusText(statusValue);
          const statusColor = getStatusColor(statusValue);
          const avatarUrl =
            mentee?.headshot ||
            mentee?.photo ||
            mentee?.picture ||
            mentee?.displayPicture ||
            'https://placehold.co/60x60';
          const stats = calculateMenteeStats(mentee);
          const objectiveName =
            mentee?.matchedObjectives?.[0]?.objective ||
            mentee?.objective ||
            'Match ativo';

          return (
            <Box
              key={key}
              sx={{
                width: '100%',
                background: 'var(--Fill-Card-card-highlight, #1C1C1C)',
                borderRadius: 1.5,
                border: '1px solid #2A2A2A',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                outlineOffset: '-1px'
              }}
            >
              <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box
                    component="img"
                    src={avatarUrl}
                    alt={getDisplayName(mentee)}
                    sx={{ width: 60, height: 60, borderRadius: 1 }}
                  />
                  <Box display="flex" flexDirection="column" gap={0.5} minWidth={0}>
                    <Typography
                      sx={{
                        color: '#FDFDFD',
                        fontSize: 18,
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {getDisplayName(mentee)}
                    </Typography>
                    <Typography sx={{ color: statusColor || '#868686', fontSize: 12, fontWeight: 600 }}>
                      {statusLabel}
                    </Typography>
                    <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>{objectiveName}</Typography>
                  </Box>
                </Box>
                <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
                  <Button
                    variant="outlined"
                    sx={{
                      color: '#FDFDFD',
                      borderColor: '#444444',
                      px: 2,
                      py: 0.5,
                      fontFamily: 'Inter',
                      fontSize: 12,
                      '&:hover': { borderColor: '#FDFDFD' }
                    }}
                    onClick={() => handleUserClick(mentee)}
                  >
                    Ver lições
                  </Button>
                  <Typography sx={{ color: '#868686', fontSize: 12 }}>
                    {stats.messagesShared} mensagens compartilhadas
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 110,
                    p: 1,
                    borderRadius: 1,
                    background: 'var(--Fill-background, #0F0F0F)'
                  }}
                >
                  <Typography sx={{ color: '#FDFDFD', fontWeight: 600 }}>{stats.messagesShared}</Typography>
                  <Typography sx={{ color: '#868686', fontSize: 12 }}>Mensagens</Typography>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 110,
                    p: 1,
                    borderRadius: 1,
                    background: 'var(--Fill-background, #0F0F0F)'
                  }}
                >
                  <Typography sx={{ color: '#FDFDFD', fontWeight: 600 }}>{stats.meetingHistory}</Typography>
                  <Typography sx={{ color: '#868686', fontSize: 12 }}>Reuniões</Typography>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 110,
                    p: 1,
                    borderRadius: 1,
                    background: 'var(--Fill-background, #0F0F0F)'
                  }}
                >
                  <Typography sx={{ color: '#FDFDFD', fontWeight: 600 }}>{stats.futureMeetings}</Typography>
                  <Typography sx={{ color: '#868686', fontSize: 12 }}>Próximas</Typography>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  const handleAddExpertise = () => {
    setRecruitObjId('');
    setRecruitObjectiveName('');
    setRecruitModalOpen(true);
  };

  const programName = grpDetails?.name || 'Mentorship Program';
  const viewDescription =
    'Find mentees whose objectives align with your expertise, message each one and schedule a mentorship meeting with them.';
  const headerDescription = isHybrid
    ? 'Matches exclusivos do programa. Clique em um mentee para ver as lições que ele concluiu.'
    : viewDescription;

  const getMatchId = (mentee: any): string | null => {
    return mentee.matchId || null; // Directly access matchId from the mentee object
  };

  const handleNavigateToChat = () => {
    let communicationId = _.get(selectedMember, 'communicationId', '');
    const listedInCommunity = communityMembers?.find((each) => each?.id?.communicationId === communicationId);
    let isInCommunity = false;
    if (listedInCommunity) {
      isInCommunity = true;
    }
    navigateToChat(communicationId, isInCommunity, personalThreadsArr, navigate, dispatch);
  };

  const { refetch: fetchGroupDetails } = useGroupDetails(groupId, getUserDetails().location);

  useEffect(() => {
    try {
      if (groupId) {
        fetchGroupDetails();
      }
    } catch (error) {
      console.log(error);
    }
  }, [groupId]);

  useEffect(() => {
    if (selectedMember) {
      handleNavigateToChat();
    }
  }, [selectedMember]);

  useEffect(() => {
    if (typeof groupId === 'string' && groupId !== '') {
      getMentorsPeersHomePage(orgId, location, groupId);
      if (isMentorship) {
        getObjectiveMentees({ orgId, location, groupId });
      } else {
        getMyMenteesList({ orgId, groupId });
      }
    }
  }, [groupId, reloadFlag, isMentorship, isHybrid]);

  useEffect(() => {
    if (groupId) {
      fetchObjectives();
    }
  }, [groupId, fetchObjectives]);

  const getMentorHelpfulTips = async () => {
    const response = await API.getMentorHelpfulTips({ orgId: orgId });
    if (response.status === 200) {
      setMentorshipHours(response?.data?.mentorshipHours);
      const formattedMentorROI = new Intl.NumberFormat('en-US', {
        notation: 'compact',
        maximumFractionDigits: 1
      }).format(response?.data?.mentorROI);

      setMentorRoi(formattedMentorROI);
    }
  };

  useEffect(() => {
    getMentorHelpfulTips();
  }, []);

  const handleRecruitModalOpen = (open: boolean) => {
    setRecruitModalOpen(open);
  };

  const handleUserClick = async (each: any) => {
    if (!isAgileExec) {
      try {
        const acsUser = getAcsUserByCommunicationId(communityMembers, _.get(each, 'communicationId'));
        const coid = _.get(acsUser, 'id.coId', '');
        dispatch(taskActions.setUserId({ userId: coid }));
        dispatch(taskActions.setGroupId({ groupId: groupId }));
        const result = await API.getAllTasks({ grpId: groupId, userId: coid });
        const data = _.get(result.data, 'data', []);
        const levels = _.get(_.head(data), 'levels');
        const levelId = _.get(_.head(levels), '_id');
        const tasks = _.get(_.head(levels), 'tasks');
        const taskId = _.get(_.head(tasks), '_id');
        if (levelId && taskId) {
          const encodedName = encodeURIComponent(getDisplayName(each) || '');
          navigate(`/app/home/mentee/level/${levelId}/task/${taskId}?userName=${encodedName}`);
        }
      } catch (err) {
        toast.error(_.get(err, 'response.data.message', ''));
      }
    } else {
      navigateToChat(each?.communicationId, true, personalThreadsArr, navigate, dispatch);
    }
  };

  const handleProfileClick = async (each: any) => {
    let specificUserDetails = null;
    if (each.id || each.menteeId) {
      const response = await API.getUserDetailsById(each.id || each.menteeId);
      specificUserDetails = response?.data?.data;
    }
    try {
      const userWithDetails = {
        ...each,
        userDetails: specificUserDetails || each
      };
      setSelectedMenteeInfo(userWithDetails);
      setOpenUserProfileDetails(true);
    } catch (error) {
      console.error('Error handling profile click:', error);
      setSelectedMenteeInfo(each);
      setOpenUserProfileDetails(true);
    }
  };

  // const getBio = (mem: any) => {
  //   const { major = '', university = '' } = _.get(mem, 'bio.education', {});
  //   const { role = '', companyName = '' } = _.get(mem, 'bio.workHistory', {});
  //   return mem?.category === 'Student' ? bioFormatter(major, university) : bioFormatter(role, companyName);
  // };

  // const handleArticleClick = (article_id: any) => {
  //   navigate('/app/library', { state: { navigatedArticleId: article_id } });
  // };

  if (groupList.error) {
    return <Box>{groupList.errorText}</Box>;
  }

  if (groupList.data?.length === 0) {
    return <Box>No groups found</Box>;
  }
  // if (groupList.data) {
  //   const index = groupList.data?.findIndex(
  //     (each) => each.id === selectedGroupId.id && each.role === selectedGroupId.role
  //   );
  //   if (index && index === -1) {
  //     window.location.replace('/');
  //     return null;
  //   }
  // }

  // const profileDetails = useProfileDetails();
  // const userName = profileDetails.firstName + ' ' + profileDetails.lastName;

  if (loadingMentor) {
    return <AppLoader />;
  }

  return (
    <>
      <Box className="bodyBoxMentor" sx={{padding: '25px', height: '100%'}}> 
        <Box
          sx={{
            width: '100%',
            minHeight: '100%',
            // px: { xs: 2, md: 3 },
            // pt: { xs: 2, md: 3 },
            // pb: { xs: 10, md: 10 },
            padding: '25px',
            borderRadius: 2,
            background: '#151515',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            overflow: 'hidden'
          }}
        >
          <Typography sx={{ color: '#FDFDFD', fontSize: 18, fontWeight: 600 }}>{programName}</Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              gap: 2
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                gap: 0.5
              }}
            >
              {!isHybrid ? (
                <>
                  <Typography
                    sx={{
                      color: '#B6B6B6',
                      fontSize: 14,
                      fontWeight: 400
                    }}
                  >
                    {expertiseLabel}
                  </Typography>
                  <Typography
                    sx={{
                      color: '#F79420',
                      fontSize: 26,
                      fontWeight: 600,
                      textTransform: 'capitalize',
                      lineHeight: '31.20px'
                    }}
                  >
                    {expertiseTitle}
                  </Typography>
                  <Typography
                    sx={{
                      color: '#868686',
                      fontSize: 20,
                      fontWeight: 600
                    }}
                  >
                    My Mentees for this Area of Expertise:
                  </Typography>
                </>
              ) : (
                <>
                  <Typography
                    sx={{
                      color: '#B6B6B6',
                      fontSize: 14,
                      fontWeight: 400
                    }}
                  >
                    Matches
                  </Typography>
                  <Typography
                    sx={{
                      color: '#F79420',
                      fontSize: 26,
                      fontWeight: 600,
                      textTransform: 'none',
                      lineHeight: '31.20px'
                    }}
                  >
                    Correspondências do programa híbrido
                  </Typography>
                  <Typography
                    sx={{
                      color: '#868686',
                      fontSize: 14,
                      fontWeight: 500
                    }}
                  >
                    O programa masterclass&mentorship não utiliza objetivos compartilhados — cada match é independente.
                  </Typography>
                </>
              )}
            </Box>
            <Box
              sx={{
                width: { xs: '100%', md: 400 },
                p: 2.5,
                background: '#0F0F0F',
                borderRadius: 1.25,
                display: 'flex',
                alignItems: 'flex-start',
                flexDirection: 'column',
                gap: 1.5
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: 1,
                    background: '#1C1C1C',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      border: '2px solid #FDFDFD',
                      borderRadius: 0
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    color: '#FDFDFD',
                    fontSize: 10,
                    fontWeight: 700
                  }}
                >
                  Stay focused on creating impact
                </Typography>
              </Box>
              <Typography sx={{ color: '#FDFDFD', fontSize: 10 }}>{headerDescription}</Typography>
            </Box>
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1
            }}
          >
            <Box
              sx={{
                flex: 1,
                pt: 6,
                pb: 6,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                gap: 2,
                flexWrap: 'wrap'
              }}
            >
              {isHybrid ? renderHybridMatchesList() : renderMenteeCard()}
              {/* {primaryMentee && (
                <Box
                  sx={{
                    width: 344,
                    height: 321,
                    p: 5,
                    borderRadius: 1.5,
                    background: 'var(--Fill-Card-card-highlight, #1C1C1C)',
                    border: '1px solid #2A2A2A',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    cursor: activeObjectiveId ? 'pointer' : 'not-allowed'
                  }}
                  onClick={() => {
                    if (!activeObjectiveId) return;
                    handleFindNewMentee(activeObjectiveId, expertiseTitle);
                  }}
                >
                  <Typography sx={{ fontSize: 22, fontWeight: 600, color: '#B6B6B6', textTransform: 'capitalize' }}>
                    Add Mentee
                  </Typography>
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: '50%',
                      background: '#1C1C1C',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <AddIcon sx={{ width: 24, height: 24, color: '#FDFDFD' }} />
                  </Box>
                </Box>
              )} */}
            </Box>
          </Box>
          {!isHybrid && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                background: '#2A2A2A',
                borderRadius: 2,
                boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.55)',
                py: 1.5,
                px: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  opacity: canGoPrev ? 1 : 0.5,
                  cursor: canGoPrev ? 'pointer' : 'not-allowed'
                }}
                onClick={canGoPrev ? handlePrevExpertise : undefined}
              >
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <ArrowBackIcon
                    sx={{
                      fontSize: 14,
                      color: canGoPrev ? '#FDFDFD' : '#444444'
                    }}
                  />
                </Box>
                <Typography sx={{ color: '#FDFDFD', fontSize: 12, fontWeight: 600 }}>Last Expertise</Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  opacity: canGoNext ? 1 : 0.5,
                  cursor: canGoNext ? 'pointer' : 'not-allowed'
                }}
                onClick={canGoNext ? handleNextExpertise : undefined}
              >
                <Typography sx={{ color: '#FDFDFD', fontSize: 12, fontWeight: 600 }}>Next Expertise</Typography>
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <ArrowForwardIcon
                    sx={{
                      fontSize: 14,
                      color: canGoNext ? '#FDFDFD' : '#444444'
                    }}
                  />
                </Box>
              </Box>
            </Box>
          )}
          {!isHybrid && (
            <Box
              sx={{
                position: 'absolute',
                top: '40%',
                left: 0,
                transform: 'translateY(-50%)',
                height: 100,
                borderRadius: '0 4px 4px 0',
                border: '1px solid #2A2A2A',
                background: canGoPrev ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 1,
                cursor: canGoPrev ? 'pointer' : 'default'
              }}
              onClick={canGoPrev ? handlePrevExpertise : undefined}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  // borderRadius: '50%',
                  // border: `2px solid ${canGoPrev ? '#FDFDFD' : '#444444'}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <ArrowBackIosNewOutlinedIcon
                  sx={{
                    color: canGoPrev ? '#FDFDFD' : '#444444',
                    fontSize: 16
                  }}
                />
              </Box>
            </Box>
          )}
          {!isHybrid && (
            <Box
              sx={{
                position: 'absolute',
                top: '40%',
                right: 0,
                transform: 'translateY(-50%)',
                height: 100,
                borderRadius: '4px 0 0 4px',
                border: '1px solid #2A2A2A',
                background: canGoNext ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 1,
                cursor: canGoNext ? 'pointer' : 'default'
              }}
              onClick={canGoNext ? handleNextExpertise : undefined}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  // borderRadius: '50%',
                  // border: `2px solid ${canGoNext ? '#FDFDFD' : '#444444'}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <ArrowForwardIosOutlinedIcon
                  sx={{
                    color: canGoNext ? '#FDFDFD' : '#444444',
                    fontSize: 16
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <Popover
        id={idMatchesTablePopover}
        open={openMatchesTablePopover}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <Box
          className="PopoverListStyle"
          onClick={() => {
            const matchId = getMatchId(selectedPopoverMentee);
            if (matchId) {
              handleOpenMatchCancel(matchId);
            }
            setAnchorEl(null);
          }}
        >
          <DeleteIcon />
          <Typography color="primary">Cancel match</Typography>
        </Box>
      </Popover>
      {/* Group of Peers Modal */}
      {isPeersPopupOpen && (
        <UserPeersPopup
          open={isPeersPopupOpen}
          onClose={() => handlePeersPopup(false)}
          peersList={peersList}
          onProfileClick={(mid) => moveToUsercommunityProfile(mid)}
          onChatClick={(each) => navigateMember(each)}
          onCalendarClick={(each) => handleCalendarOpen(each)}
          communityMembers={communityMembers} // Pass the communityMembers prop here
          navigate={navigate} // Pass the navigate prop here
        />
      )}

      {/* Request Match dialog */}
      <RequestMatch openRequestMatch={openRequestMatch} handleRequestMatch={handleRequestMatch} />

      {/* Request Match Reject dialog */}
      <RequestMatchReject
        openRequestMatchReject={openRequestMatchReject}
        handleRequestMatchReject={handleRequestMatchReject}
        requestId={requestId}
        groupId={groupId}
        mentorId={mentorId}
        menteeId={menteeId}
        doReload={doReload}
        commId={commId}
      />

      {/* Notes for Mallory Capoferri */}
      {/* <UserNotesPopup
        open={openUserNotes}
        handleOpen={handleOpenUserNotes}
      /> */}

      {/* Objective Modal */}
      <ObjectiveModal
        open={isObjectiveModalOpen}
        title={objective?.objective}
        onClose={handleCloseObjectiveModal}
        onSubmit={(data: { name: string; progress: number }) => {
          return Promise.resolve();
        }}
        description={objective?.description}
        status={objective?.status}
        objId={objective?._id}
        notes={objective?.notes}
        mentorsList={objective?.mentors?.filter((each: any) => each.userId === objUserId)}
        myMenteesList={selectedMentee}
        matchedObjectives={matchedObjectives}
      />

      {/* Cancel Match Modal */}
      <MatchCancelModal
        openMatchCancel={openMatchCancel}
        handleMatchCancel={handleCloseMatchCancel} // Close modal on cancel
        confirmMatchId={selectedMatchId}
        refetch={refetch}
      />

      {/* Match Cancel Confirmation Modal */}
      {/* <ConfirmationModal
        open={openMatchCancel}
        onClose={handleCloseMatchCancel}
        onConfirm={() => {
          handleCloseMatchCancel();
        }}
        message="Are you sure you want to cancel?"
        messageStyle={{ py: 2 }}
        confirmText="Yes"
      /> */}

      {/* Calendar Modal */}
      <CallBookingModal
        title={objective?.objective}
        handleCloseModal={handleCalendarClose}
        openFlag={openCalendarModal}
        chatProfileName={displayName}
        threadParticipantsUsers={participantUser}
        chatType="Chats"
        pageName="Mentor"
        bookedCallDetails={bookedCallDetails}
        onMeetingScheduled={handleMeetingScheduled}
      />

      {recruitModalOpen && (
        <RecruitNewMentor
          title=""
          open={recruitModalOpen}
          onClose={() => setRecruitModalOpen(false)}
          mentorsList={recruitMentors}
          onProfileClick={(mid: any) => moveToUsercommunityProfile(mid)}
          onChatClick={(each: any) => navigateMember(each)}
          onCalendarClick={(each: any) => handleCalendarOpen(each)}
          objId=""
          grpId={groupId}
          apiType="mentee"
          learningObjective={activeObjective}
        />
      )}

      {/* Task Modal */}
      {/* Welcome Modals */}
      {/* <WelcomeModals open={openGettingStartedModal} handleClickWelcomeModals={handleClickWelcomeModals} /> */}

      {/* User Profile Details Popup */}
      <UserProfileDetailsPopup
        loading={false} // loader
        openUserProfileDetails={openUserProfileDetails}
        handleopenUserProfileDetails={handleopenUserProfileDetails}
        userData={selectedMenteeInfo}
      />
      {/* <MenteeProfileModal
        open={openUserProfileDetails}
        onClose={() => setOpenUserProfileDetails(false)}
        user={selectedMenteeInfo}
      /> */}
    </>
  );
};

export default Mentor;
